// Смена вида записи — человек ↔ организация (005-2: data-model.md, contracts/api.md). Прежняя
// запись получает состояние «вид сменён» (rekinded), упоминания и псевдонимы переходят на цель;
// «Вернуть вид» восстанавливает их точно. Вопрос о виде («Похоже на организацию») считается
// правилами FR-002 005 при чтении; хранится только ответ «Вид верный» (research, Р9).
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { RecordKindSchema, RecordRefSchema } from './record.ts';

const count = z.number().int().min(0);

/** Вид и номер записи — без имени: тело запроса. */
export const RecordKeySchema = z.object({ kind: RecordKindSchema, id: IdSchema });
export type RecordKey = z.infer<typeof RecordKeySchema>;

/** Состояние прежней записи до смены вида: в него возвращает «Вернуть вид». */
export const kindChangeFromStates = ['draft', 'confirmed'] as const;
export const KindChangeFromStateSchema = z.enum(kindChangeFromStates);

/** Сколько перешло на цель при смене вида. */
export const KindChangeMovedSchema = z.object({ mentions: count, aliases: count });
export type KindChangeMoved = z.infer<typeof KindChangeMovedSchema>;

export const KindChangeSchema = z.object({
  id: IdSchema,
  from: RecordRefSchema.extend({ state: KindChangeFromStateSchema }),
  to: RecordRefSchema,
  /** Цель создана этой сменой; иначе — объединение с существующей записью. */
  created: z.boolean(),
  moved: KindChangeMovedSchema,
  at: TimestampSchema,
  by: PersonRefSchema,
  undoneAt: TimestampSchema.nullable(),
  /** После возврата цель ушла из справочника: своих данных у неё не осталось (FR-013). */
  targetHidden: z.boolean(),
});
export type KindChange = z.infer<typeof KindChangeSchema>;

/** Числа окна «Вернуть вид» (FR-013). */
export const KindUndoCountsSchema = z.object({
  /** Вернутся на прежнюю запись — перешедшее и привязанное позже тем же написанием. */
  returning: KindChangeMovedSchema,
  /** Останутся у цели — её собственное. */
  staying: z.object({
    mentions: count,
    aliases: count,
    emails: count,
    workplaces: count,
    curatorships: count,
  }),
  targetHidden: z.boolean(),
});
export type KindUndoCounts = z.infer<typeof KindUndoCountsSchema>;

/** Смена вида в карточке: эта запись прежняя (from) или цель (to) последней невозвращённой смены. */
export const RecordKindChangeSchema = z.object({
  change: KindChangeSchema,
  role: z.enum(['from', 'to']),
  undo: KindUndoCountsSchema,
});
export type RecordKindChange = z.infer<typeof RecordKindChangeSchema>;

/**
 * Причины вопроса о виде (правила FR-002 005, mentions/kind.ts): у человека — признаки
 * организации, у организации — «Фамилия И. О.».
 */
export const kindDoubtReasons = [
  'legal_form',
  'quotes',
  'organization_word',
  'dictionary',
  'digits',
  'camel',
  'name_part',
  'lone_contractor',
  'person_name',
  'ai_estimate',
] as const;
export type KindDoubtReason = (typeof kindDoubtReasons)[number];
export const KindDoubtReasonSchema = z.enum(kindDoubtReasons);

/** Причина вопроса словами: «Похоже на организацию: …». */
export const kindDoubtReasonLabels = {
  legal_form: 'в названии форма собственности',
  quotes: 'название в кавычках',
  organization_word: 'слова «подрядчик», «администрация», «управление» или «отдел»',
  dictionary: 'название из словаря организаций',
  digits: 'цифры в названии',
  camel: 'заглавная буква внутри слова',
  name_part: 'часть названия: «…проект», «…строй», «…надзор», «Энерго…»',
  lone_contractor: 'одно слово в колонке «Контрагент»',
  person_name: 'фамилия и инициалы',
  ai_estimate: 'по оценке ИИ',
} as const satisfies Record<KindDoubtReason, string>;
